/**
 * Einheitliches Modell für alles, was im Text markiert ist (#2267):
 * Anmerkungen mit Notiz und Markierungen ohne Notiz, aus statischen
 * Beiträgen (Tabelle annotations) wie aus Collab-Beiträgen
 * (Anmerkungs-Sections). Legende, Übersichtsleiste, Panel und Tasten
 * arbeiten nur damit.
 */
import type { MarginItem } from '../components/AnnotationMargin'
import { colorOf, isAgentId } from './annotationAuthors'

export interface AnnotationEntry extends MarginItem {
  kind: 'note' | 'highlight'
  /** Die markierte Passage, so wie sie beim Anlegen stand. */
  quote: string
}

export interface EntryAuthor {
  key: string
  name: string
  color: string
  agent: boolean
  count: number
}

export const authorKey = (e: { author_id?: string | null; author_name: string }): string =>
  e.author_id || `name:${e.author_name}`

export function entryKind(noteText: string | null | undefined): 'note' | 'highlight' {
  return (noteText || '').trim() ? 'note' : 'highlight'
}

/** Autoren mit Farbe und Anzahl, die Reihenfolge folgt dem ersten Auftreten. */
export function entryAuthors(entries: AnnotationEntry[]): EntryAuthor[] {
  const map = new Map<string, EntryAuthor>()
  for (const e of entries) {
    const key = authorKey(e)
    const cur = map.get(key)
    if (cur) cur.count += 1
    else map.set(key, { key, name: e.author_name, color: colorOf(e.author_id, e.author_name), agent: isAgentId(e.author_id), count: 1 })
  }
  return [...map.values()]
}

/** Erstes Element der Markierung im gerenderten Text. */
export function anchorElement(root: HTMLElement | null, id: number | string): HTMLElement | null {
  if (!root) return null
  return root.querySelector<HTMLElement>(`[data-annotation-id="${CSS.escape(String(id))}"]`)
}

/** Dokumentreihenfolge; Einträge ohne auffindbare Stelle stehen am Ende. */
export function orderByDocument(root: HTMLElement | null, entries: AnnotationEntry[]): AnnotationEntry[] {
  const pos = new Map<string, number>()
  if (root) {
    let i = 0
    root.querySelectorAll<HTMLElement>('[data-annotation-id]').forEach((el) => {
      const id = el.dataset.annotationId || ''
      if (!pos.has(id)) pos.set(id, i++)
    })
  }
  return [...entries].sort(
    (a, b) => (pos.get(String(a.id)) ?? Number.MAX_SAFE_INTEGER) - (pos.get(String(b.id)) ?? Number.MAX_SAFE_INTEGER),
  )
}

function quoteLines(text: string): string {
  return text.trim().split('\n').map((l) => `> ${l}`).join('\n')
}

/** Auszug als Markdown: Passage als Zitat, darunter Notiz und Antworten. */
export function entriesToMarkdown(title: string, entries: AnnotationEntry[]): string {
  const out: string[] = [`## Markierungen: ${title}`, '']
  if (entries.length === 0) out.push('Keine Markierungen.', '')
  for (const e of entries) {
    if (e.quote.trim()) out.push(quoteLines(e.quote), '')
    if (e.kind === 'note') out.push(`**${e.author_name}:** ${e.note_text.trim()}`, '')
    else out.push(`*Markiert von ${e.author_name}*`, '')
    for (const r of e.replies) out.push(`- **${r.author_name || 'Unbekannt'}:** ${r.text.trim()}`)
    if (e.replies.length) out.push('')
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd() + '\n'
}
